import request from "@/providers/requests";
import ENDPOINTS from "@/providers/end-points";
import Storage from "@/services/storage";

// Interface
import { Response } from "@/interface/response.interface";
import { SummaryItemInterface, TaxInterface } from "@/interface/business/item.interface";
import { PaymentMethodInterface } from "@/interface/business/payment.interface";
import { DiscountInterface, SaleDiscountsInterface } from "@/interface/business/discount.interface";
import { LocationInterface } from "@/interface/business/location.interface";

// Types
import { LocationType } from "@/types/business/location.type";
import { CategoryType } from "@/types/business/category.type";
import { PaymentMethodType } from "@/types/business/payment.type";
import { ItemType, TaxType } from "@/types/business/item.type";

// Modules
import { Location } from "@/modules/location";
import { StorageConstants } from "@/enums/storage.enum";
import { PaymentMethod } from "@/modules/business/payment-method";
import { Category } from "@/modules/business/category.ts";
import Discount from "@/modules/business/discount/discount";
import Item from "@/modules/location/item.ts";
import { Tax } from "@/modules/business/Tax";
import { DiscountType } from "@/types/business/discount.type";
import { ExtraChargesType } from "@/types/business/extraCharges.Type";
import { ExtraChargesInterface } from "@/interface/business/extraCharges.interface";
import { ExtraCharge } from "@/modules/business/extra-charges";
import { Back4AppInterface, BusinessInterface } from "@/interface/business/business";
import { Business } from "@/modules/business/business";
import { BusinessType } from "@/types/business/business";
import { UserType } from "@/types/user.type";
import { UserInterface } from "@/interface/user.interface";
import { UserModel } from "@/modules/user";
import Back4App from "@/services/back4app";
import { MODES } from "@/enums/apps.enum";
import { SaleDiscounts } from "@/modules/business/discount/discounts";
import { ExchangeInterface } from "@/interface/business/exchange.interface";
import { ExchangeType } from "@/types/business/exchange.type";
import { Exchange } from "@/modules/business/exchange";
import { CurrencyType } from "@/types/business/currency.type";
import { Currency } from "@/modules/business/currency";
import { PaymentCategoryType } from "@/types/business/paymentCategory.type";
import PaymentCategory from "@/modules/business/payment-category";

/**
 * Business Store Module
 */
export default {
  namespaced: true,

  state: {
    locations: [],
    location: {},
    categories: [],
    specificCategories: [],
    specificItems: [],
    items: [],
    categoryItemsList: [],
    paymentMethods: [],
    category: null,
    discounts: [],
    taxes: [],
    taxesCount: 0,
    extraCharges: [],
    extraCharge: null,
    success: false,
    message: "",
    extraChargesCount: 0,
    business: null,
    agents: [],
    locationsNames: null,
    customerDiscounts: [],
    back4App: null,
    discountsCount: 0,
    saleDiscount: null,
    exchanges: [],
    currencies: [],
    paymentCategories: [],
  },

  getters: {
    locationsList: (state: any): Array<LocationInterface> => {
      if (!!state.locations?.length) {
        return state.locations;
      }
      return Storage.get(StorageConstants.LOCATIONS).map((location) => new Location(location));
    },
    location: (state: any): Object => state.location,
    locationsNames: (state: any): Object => {
      const locations = !!state.locations?.length ? state.locations : Storage.get(StorageConstants.LOCATIONS);
      const locationsObject = {};
      locations?.forEach((location) => {
        locationsObject[location.id] = location;
      });
      state.locationsNames = locationsObject;
      return state.locationsNames;
    },
    categoriesList: (state: any): Array<CategoryType> => state.categories,
    specificCategoriesList: (state: any): Array<CategoryType> => state.specificCategories,
    specificItemsList: (state: any): Array<ItemType> => state.specificItems,
    itemsList: (state: any): Array<ItemType> => state.items,
    categoryItemsList: (state: any): Array<SummaryItemInterface> => state.categoryItemsList,
    paymentMethods: (state: any): Array<PaymentMethodInterface> => state.paymentMethods,
    discountList: (state: any): Array<DiscountInterface> => state.discounts,
    discountsCount: (state: any): number => state.discountsCount,
    SaleDiscount: (state: any): SaleDiscountsInterface => state.saleDiscount,
    taxesList: (state: any): Array<TaxInterface> => state.taxes,
    taxesCount: (state: any): number => state.taxesCount,
    extraChargesList: (state: any): Array<ExtraChargesInterface> => state.extraCharges,
    success: (state: any): boolean => state.success,
    message: (state: any): string => state.message,
    extraChargesCount: (state: any): number => state.extraChargesCount,
    extraCharge: (state: any): any => state.extraCharge,
    agents: (state: any): Array<UserType> => state.agents,
    customerDiscounts: (state: any): Array<DiscountInterface> => state.customerDiscounts,
    business: (state: any): BusinessInterface => {
      if (state.business) {
        return state.business;
      }

      return new Business(Storage.get(StorageConstants.BUSINESS));
    },
    back4App: (state: any): Back4AppInterface => state.back4App,
    exchangesList: (state: any): Array<ExchangeInterface> => state.exchanges,
    currenciesList: (state: any): Array<ExchangeInterface> => state.currencies,
    paymentCategoriesList: (state: any): any => state.paymentCategories,
  },

  mutations: {
    SET_LOCATIONS_LIST(state: any, payload) {
      state.locations = payload;
    },

    SET_LOCATION_DETAILS(state: any, payload) {
      state.location = payload;
    },

    SET_PAYMENTS_METHODS_LIST(state: any, payload = []) {
      state.paymentMethods = payload;
    },

    SET_DISCOUNT_LIST(state: any, payload) {
      state.discounts = payload.discounts;
      state.discountsCount = payload?.count || 0;
    },

    SET_DISCOUNT_UPDATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_CATEGORY_LIST(state: any, payload) {
      state.categories = payload;
    },

    SET_SPECIFIC_CATEGORIES(state: any, payload) {
      state.specificCategories = payload;
    },

    SET_ITEMS_LIST(state: any, payload) {
      state.items = payload;
    },

    SET_SPECIFIC_ITEMS(state: any, payload) {
      state.specificItems = payload;
    },

    SET_EXTRA_CHARGES_LIST(state: any, { list = [], count = 0 }) {
      state.extraCharges = list;
      state.extraChargesCount = count;
    },

    SET_EXTRA_CHARGES_UPDATED(state: any, payload) {
      state.extraCharge = payload.charge;
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_TAXES_LIST(state: any, payload) {
      state.taxes = payload[0];
      state.taxesCount = payload[1];
    },

    SET_TAX_UPDATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_BUSINESS(state: any, payload) {
      state.business = payload;
    },

    SET_AGENTS_LIST(state: any, payload) {
      state.agents = payload;
    },

    SET_CUSTOMER_DISCOUNT_LIST(state: any, payload) {
      state.customerDiscounts = payload;
    },

    SET_BRANCHES_STATUES(state: any) {
      if (!state.back4App) {
        state.back4App = new Back4App();
      }
    },
    DESTROY_BRANCHES_STATUES(state: any) {
      if (!!state.back4App) {
        state.back4App.unSubscribe();
      }
      delete state.back4App;
    },

    SET_SALE_DISCOUNT(state: any, saleDiscount) {
      state.saleDiscount = saleDiscount;
    },

    SET_EXCHANGES_LIST(state: any, payload) {
      state.exchanges = payload;
    },

    SET_EXCHANGE_UPDATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },

    SET_CURRENCIES_LIST(state: any, payload) {
      state.currencies = payload;
    },

    SET_PAYMENT_CATEGORIES_LIST(state: any, payload) {
      state.paymentCategories = payload;
    },

    SET_PAYMENT_CATEGORY_UPDATED(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message || "";
    },
  },

  actions: {
    async getBusiness({ commit }) {
      return await request
        .get<Response<BusinessType>>(ENDPOINTS.BUSINESS)
        .then((response) => response.data)
        .then(async ({ data }) => {
          Storage.set(StorageConstants.BUSINESS, data);
          await commit("SET_BUSINESS", new Business(data));
        })
        .catch(async (error) => {
          console.log(error);
        });
    },
    async updateBusiness({ commit }, business) {
      return await request
        .patch<Response<BusinessType>>(ENDPOINTS.BUSINESS, business)
        .then((response) => response.data)
        .then(async ({ data }) => {
          // Storage.set(StorageConstants.BUSINESS, data);
          // await commit("SET_BUSINESS", new Business(data));
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getLocations({ commit }: { commit: any }) {
      return await request
        .get<Response<Array<LocationType>>>(ENDPOINTS.LOCATIONS)
        .then((response) => response.data)
        .then(async ({ data }) => {
          const locations = data.map((item) => new Location(item));
          Storage.set(StorageConstants.LOCATIONS, data);
          commit("SET_LOCATIONS_LIST", locations);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getLocation({ commit }: { commit: any }, locationId) {
      return await request
        .get<Response<Array<LocationType>>>(`${ENDPOINTS.LOCATION_DETAILS(locationId)}`)
        .then((response) => response.data)
        .then(async ({ data }) => {
          commit("SET_LOCATION_DETAILS", data);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getCategories({ commit }: { commit: any }, payload) {
      const { page = 1, page_size = 32, search = "" } = payload;
      return await request
        .get<Response<Array<CategoryType>>>(ENDPOINTS.CATEGORIES(page, page_size, search))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const categories = data.map((item) => new Category(item));
          commit("SET_CATEGORY_LIST", categories);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getSpecificCategories({ commit }: { commit: any }, payload) {
      const { page = 1, page_size = 32, categories = "" } = payload;
      return await request
        .get<Response<Array<CategoryType>>>(ENDPOINTS.SPECIFIC_CATEGORIES(page, page_size, categories))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const categories = data.map((item) => new Category(item));
          commit("SET_SPECIFIC_CATEGORIES", categories);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getItems({ commit }: { commit: any }, payload) {
      const { page = 1, page_size = 32, search = "" } = payload;
      return await request
        .get<Response<Array<ItemType>>>(ENDPOINTS.ALL_ITEMS(page, page_size, search))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const items = data.map((item) => new Item(item));
          commit("SET_ITEMS_LIST", items);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getSpecificItems({ commit }: { commit: any }, payload) {
      const { page = 1, page_size = 32, items = "" } = payload;
      return await request
        .get<Response<Array<ItemType>>>(ENDPOINTS.SPECIFIC_ITEMS(page, page_size, items))
        .then((response) => response.data)
        .then(async ({ data }) => {
          const items = data.map((item) => new Item(item));
          commit("SET_SPECIFIC_ITEMS", items);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    /**
     * Get Branch Payments Methods
     * @param commit
     * @param locationId
     */
    async getBranchPaymentsMethods({ commit }: { commit: any }, locationId) {
      const branches = Storage.get(StorageConstants.LOCATIONS);
      const branch = branches.find((branch) => branch.id === locationId);

      const payments_methods = branch?.payment_methods?.map((method) => new PaymentMethod(method)) || [];
      return await commit("SET_PAYMENTS_METHODS_LIST", payments_methods);
    },

    async getPaymentsMethods({ commit }: { commit: any }) {
      return await request
        .get<Response<Array<PaymentMethodType>>>(ENDPOINTS.PAYMENT_METHODS)
        .then((response) => response.data)
        .then(async ({ data }) => {
          const payments_methods = data.map((method) => new PaymentMethod(method));
          commit("SET_PAYMENTS_METHODS_LIST", payments_methods);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getExtraCharges({ commit }: { commit: any }, payload) {
      const { page = 1, itemsPerPage = 32, search = "" } = payload;

      return await request
        .get<Response<Array<ExtraChargesType>>>(ENDPOINTS.EXTRA_CHARGES(search, page, itemsPerPage))
        .then((response) => response.data)
        .then(async ({ data, count }) => {
          const list = data.map((item: ExtraChargesType) => new ExtraCharge(item));
          await commit("SET_EXTRA_CHARGES_LIST", {
            list,
            count,
          });
        })
        .catch(async (error) => {
          await commit("SET_EXTRA_CHARGES_LIST", { list: [], count: 0 });
        });
    },

    async deleteExtraCharges({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.EXTRA_CHARGE}/${payload}`)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async bulkDeleteExtraCharges({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.EXTRA_CHARGE}/batch`, { data: payload })
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async createExtraCharges({ commit }: { commit: any }, payload) {
      return await request
        .post<Response<Array<ExtraChargesType>>>(ENDPOINTS.EXTRA_CHARGE, payload)
        .then((response) => response.data)
        .then(async (data) => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: true, charge: data.data });
        })
        .catch(async ({ error }) => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: false, message: error?.message });
        });
    },

    async updateExtraCharges({ commit }: { commit: any }, payload) {
      return await request
        .put<Response<Array<ExtraChargesType>>>(`${ENDPOINTS.EXTRA_CHARGE}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXTRA_CHARGES_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async getDiscounts(
      { commit }: { commit: any },
      { locationId = "", page = 1, pageSize = 1000, search = "", mode = MODES.FULL }
    ) {
      return await request
        .get<Response<Array<DiscountType>>>(
          `${ENDPOINTS.DISCOUNTS}?page=${page}&page_size=${pageSize}&location_id=${locationId}&search=${search}&mode=${mode}`
        )
        .then((response) => response.data)
        .then(async ({ data, count }) => {
          const saleDiscounts = new SaleDiscounts(data, count);
          await commit("SET_SALE_DISCOUNT", saleDiscounts);

          const discounts: Array<DiscountType> = [];

          data.forEach((discount: DiscountType) => {
            if (!discount.item_applicable) {
              discounts.push(new Discount(discount));
            }
          });

          const length = discounts?.length || 0;

          await commit("SET_DISCOUNT_LIST", { discounts, count: length });
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getDiscountList(
      { commit }: { commit: any },
      { locationId = "", page = 1, pageSize = 1000, search = "", mode = MODES.FULL }
    ) {
      return await request
        .get<Response<Array<DiscountType>>>(
          `${ENDPOINTS.DISCOUNTS}?page=${page}&page_size=${pageSize}&location_id=${locationId}&search=${search}&mode=${mode}`
        )
        .then((response) => response.data)
        .then(async ({ data, count }) => {
          const discounts: Array<DiscountType> = data.map((discount: DiscountType) => new Discount(discount));
          await commit("SET_DISCOUNT_LIST", { discounts, count });
        })
        .catch(async (error) => {
          await commit("SET_DISCOUNT_LIST", { discounts: [], count: 0 });
        });
    },

    async createDiscount({ commit }: { commit: any }, payload) {
      return await request
        .post<Response<Array<DiscountType>>>(ENDPOINTS.DISCOUNTS, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_DISCOUNT_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_DISCOUNT_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },
    async updateDiscount({ commit }: { commit: any }, payload) {
      return await request
        .put<Response<Array<DiscountType>>>(`${ENDPOINTS.DISCOUNTS}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_DISCOUNT_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_DISCOUNT_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async deleteDiscount({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.DISCOUNTS}/${payload}`)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_DISCOUNT_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_DISCOUNT_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async getTaxes({ commit }: { commit: any }, payload) {
      const { page = 1, itemsPerPage = 32, search = "" } = payload;

      return await request
        .get<Response<Array<TaxType>>>(`${ENDPOINTS.TAXES}?search=${search}&page=${page}&page_size=${itemsPerPage}`)
        .then((response) => response.data)
        .then(async ({ data, count }) => {
          const taxes = data.map((tax: TaxType): TaxType => new Tax(tax));
          commit("SET_TAXES_LIST", [taxes, count]);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async deleteTax({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.TAX}/${payload}`)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_TAX_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_TAX_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async createTax({ commit }: { commit: any }, payload) {
      return await request
        .post<Response<Array<TaxType>>>(ENDPOINTS.TAX, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_TAX_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_TAX_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async updateTax({ commit }: { commit: any }, payload) {
      return await request
        .put<Response<Array<TaxType>>>(`${ENDPOINTS.TAX}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_TAX_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_TAX_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async getAgents({ commit }: { commit: any }) {
      return await request
        .get<Response<Array<UserType>>>(ENDPOINTS.AGENTS())
        .then((response) => response.data)
        .then(async ({ data }) => {
          const users = data.map((user: UserType): UserInterface => new UserModel(user));
          await commit("SET_AGENTS_LIST", users);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async getPaymentCategories({ commit }: { commit: any }, payload) {
      const { locationId = "", page = 1, itemsPerPage = 32, search = "" } = payload;

      return await request
        .get<Response<Array<PaymentCategoryType>>>(`${ENDPOINTS.PAYMENT_CATEGORIES}?location_id=${locationId}&search=${search}&page=${page}&page_size=${itemsPerPage}`)
        .then((response) => response.data)
        .then(async ({ data }) => {
          const paymentCategories = data.map((paymentCategory: PaymentCategoryType): PaymentCategoryType => new PaymentCategory(paymentCategory));
          commit("SET_PAYMENT_CATEGORIES_LIST", paymentCategories);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async deletePaymentCategory({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.PAYMENT_CATEGORIES}/${payload}`)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async createPaymentCategory({ commit }: { commit: any }, payload) {
      return await request
        .post<Response<Array<PaymentCategoryType>>>(ENDPOINTS.PAYMENT_CATEGORIES, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },

    async updatePaymentCategory({ commit }: { commit: any }, payload) {
      return await request
        .put<Response<Array<PaymentCategoryType>>>(`${ENDPOINTS.PAYMENT_CATEGORIES}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_PAYMENT_CATEGORY_UPDATED", { success: false, message: error?.error?.message });
          console.log(error);
        });
    },


    async getExchanges({ commit }: { commit: any }, payload) {
      const { page = 1, itemsPerPage = 32, search = "" } = payload;
      return await request
        .get<Response<Array<ExchangeType>>>(`${ENDPOINTS.EXCHANGES}?search=${search}&page=${page}&page_size=${itemsPerPage}`)
        .then((response) => response.data)
        .then(async ({ data }) => {
          const exchanges = data.map((exchange: ExchangeType): ExchangeType => new Exchange(exchange));
          commit("SET_EXCHANGES_LIST", exchanges);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },

    async deleteExchange({ commit }: { commit: any }, payload) {
      return await request
        .delete<Response<any>>(`${ENDPOINTS.EXCHANGES}/${payload}`)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXCHANGE_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXCHANGE_UPDATED", { success: false, message: error?.error?.message });
        });
    },

    async createExchange({ commit }: { commit: any }, payload) {
      delete payload.id;
      return await request
        .post<Response<Array<CurrencyType>>>(ENDPOINTS.EXCHANGES, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXCHANGE_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXCHANGE_UPDATED", { success: false, message: error?.error?.message });
        });
    },

    async updateExchange({ commit }: { commit: any }, payload) {
      return await request
        .put<Response<Array<CurrencyType>>>(`${ENDPOINTS.EXCHANGES}/${payload.id}`, payload)
        .then((response) => response.data)
        .then(async () => {
          await commit("SET_EXCHANGE_UPDATED", { success: true });
        })
        .catch(async (error) => {
          await commit("SET_EXCHANGE_UPDATED", { success: false, message: error?.error?.message });
        });
    },

    async getCurrencies({ commit }: { commit: any }, payload) {
      const { page = 1, itemsPerPage = 32, search = "" } = payload;
      return await request
        .get<Response<Array<CurrencyType>>>(`${ENDPOINTS.CURRENCIES}?search=${search}&page=${page}&page_size=${itemsPerPage}`)
        .then((response) => response.data)
        .then(async ({ data }) => {
          const currencies = data.map((currency: CurrencyType): CurrencyType => new Currency(currency));
          commit("SET_CURRENCIES_LIST", currencies);
        })
        .catch(async (error) => {
          console.log(error);
        });
    },
  },
};
